import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { withTranslation } from 'config/i18n';
// Material UI Core
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useHandleResponse from 'utils/useHandleResponse';
import axios, { API } from 'config/axios';
import {
  Typography,
  TextField,
  DialogTitle,
  DialogContent,
  Grid,
  DialogActions,
  IconButton,
  Dialog,
  MenuItem,
  Button,
} from '@material-ui/core';
import EarningsBalanceTable from 'components/ownerPages/earningsAndPayouts/earningsBalance/EarningsBalanceTable';
import Dinero from 'dinero.js';
import {
  useLayoutState,
  useLayoutSnackbar,
  useLayoutLoadingSpinner,
} from 'components/shared/Layout';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import FormErrors from 'components/shared/FormErrors';

// Material UI icons
import CloseIcon from '@material-ui/icons/Close';

const useStyles = makeStyles((theme) => ({
  paper: {
    width: theme.typography.pxToRem(1200),
  },
  dialogContent: {
    margin: theme.spacing(2, 0),
  },
  right: {
    textAlign: 'right',
  },
  balance: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 500,
    '& $amount': {
      fontSize: theme.typography.pxToRem(28),
      fontWeight: 800,
      marginBottom: theme.spacing(1),
      [theme.breakpoints.down('md')]: {
        fontSize: theme.typography.pxToRem(20),
      },
    },
  },
  amount: {},
  dialogActions: {
    backgroundColor: theme.palette.grey.A000,
  },
  submit: {
    [theme.breakpoints.down('md')]: {
      margin: theme.spacing(2, 0, 1),
    },
  },
}));

const PayoutWithdrawDialog = (props) => {
  const classes = useStyles();
  const {
    onClose, open, t, transferMethods, ...other
  } = props;
  const theme = useTheme();

  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const { layoutState } = useLayoutState();

  const [loading, setLoading] = useLayoutLoadingSpinner();

  const [snackbar, setSnackbar] = useLayoutSnackbar();

  const [errorMsgs, setErrorMsgs] = useState(null);

  const [balance, setBalance] = useState(Dinero({ amount: 0 }));
  const [currency, setCurrency] = useState(null); // The currency of the returned payment distributions

  const [value, setValue] = useState({
    payment_distributions_ids: [],
    transfer_method_id: '',
  });

  const [data, setData] = useState(null); // unsettled payment distributions endpoint

  const { handleError } = useHandleResponse();

  const cancelToken = axios.CancelToken;
  const source = cancelToken.source();

  useEffect(() => {
    axios
      .get(API.owner.payouts.unsettledPaymentDistributions, {
        cancelToken: source.token,
      })
      .then((res) => {
        if (res?.data) {
          setData(res.data);
        }
      })
      .catch((error) => {
        handleError(error);
      });
    return () => {
      source.cancel('Operation canceled by the user.');
    };
  }, []);

  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    if (data?.payment_distributions) {
      let total = Dinero({ amount: 0 });
      data.payment_distributions.forEach((payment) => {
        if (payment.amount?.amount && payment.amount.currency_code) {
          setCurrency(payment.amount.currency_code);
          if (payment.transaction_type === 'host_penalty') {
            total = total.subtract(Dinero({ amount: payment.amount.amount }));
          }
        }
      });
      setBalance(total);
    }
  }, [data]);

  // On checkboxes changes to withdraw payments
  const handleOnChange = (e, row) => {
    const { checked } = e.target;
    setValue((prevValue) => {
      let newDistributionsIds = prevValue.payment_distributions_ids;
      if (checked) {
        newDistributionsIds.push(row.id);
        if (row.amount.amount) {
          setBalance(balance.add(Dinero({ amount: row.amount.amount })));
        }
      } else {
        newDistributionsIds = newDistributionsIds.filter((id) => +id !== +row.id);
        if (row.amount.amount) {
          setBalance(balance.subtract(Dinero({ amount: row.amount.amount })));
        }
      }
      return {
        ...prevValue,
        payment_distributions_ids: newDistributionsIds,
      };
    });
  };

  const isFieldsDisabled = () => {
    let isDisabled = true;
    if (
      balance.getAmount() > 0
      && value
      && value.payment_distributions_ids
      && value.payment_distributions_ids.length > 0
    ) {
      isDisabled = false;
    }
    return isDisabled;
  };

  const cancelToken2 = axios.CancelToken;
  const source2 = cancelToken2.source();

  const submit = () => {
    setErrorMsgs(null);
    setLoading(true);
    axios
      .post(
        API.owner.payouts.index,
        {
          authenticity_token: layoutState.authenticity_token,
          payment_distributions_ids: value.payment_distributions_ids,
          transfer_method_id: +value.transfer_method_id,
        },
        {
          cancelToken: source2.token,
        },
      )
      .then(() => {
        setSnackbar({
          ...snackbar,
          open: true,
          severity: 'success',
          message: t(
            'fe_earnings_and_payouts_page:payout_withdraw_dialog.payout_withdrawal_req_submit_successfully',
          ),
        });
        onClose(true);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        try {
          switch (error.response.status) {
            case 406:
              setErrorMsgs(error.response.data);
              break;
            default:
              handleError(error);
          }
        } catch (e) {
          handleError(error);
        }
      });
    return () => {
      source2.cancel('Operation canceled by the user.');
    };
  };

  return (
    <Dialog
      id="payout-withdraw-dialog"
      open={open}
      classes={{
        paper: classes.paper,
      }}
      maxWidth="lg"
      fullScreen={matches}
      keepMounted
      onClose={handleClose}
      {...other}
    >
      <DialogTitle>
        <Typography variant="h5">
          {t('fe_earnings_and_payouts_page:payout_withdraw_dialog.title')}
        </Typography>
        <IconButton onClick={handleClose} aria-label="Cancel" color="secondary">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h5" className={classes.balance}>
              {t('fe_earnings_and_payouts_page:payout_withdraw_dialog.balance')}
              &nbsp;
              <span className={classes.amount}>
                {`${currency || ''} ${balance.toFormat(
                  '0,0.00',
                )}`}
              </span>
            </Typography>
          </Grid>
          {errorMsgs && (
            <Grid item xs={12}>
              <FormErrors errorMsgs={errorMsgs} showBaseErrors />
            </Grid>
          )}
          <Grid item xs={12}>
            <EarningsBalanceTable
              data={data}
              balance={balance.getAmount()}
              onChange={handleOnChange}
            />
            <FormErrors errorMsgs={errorMsgs} errorField="payment_distributions_ids" />
          </Grid>
        </Grid>
      </DialogContent>
      {data && (
        <DialogActions className={classes.dialogActions}>
          <Grid container spacing={0} alignItems="flex-end">
            <Grid item md={6} xs={12}>
              <TextField
                select
                fullWidth
                id="transfer_method_id"
                required
                color="secondary"
                disabled={isFieldsDisabled()}
                label={t(
                  'fe_earnings_and_payouts_page:payout_withdraw_dialog.choose_transfer_method',
                )}
                variant="outlined"
                name="transfer_method_id"
                type="text"
                value={value.transfer_method_id || ''}
                onChange={(e) => setValue({
                  ...value,
                  transfer_method_id: e.target.value,
                })}
                error={Boolean(errorMsgs?.errors && errorMsgs.errors.transfer_method_id)}
              >
                {transferMethods?.bank_accounts ? (
                  transferMethods.bank_accounts.map((option) => {
                    const name = option.bank_name;
                    const { time } = option.created_at;
                    const zone = option.created_at.time_zone;
                    return (
                      <MenuItem
                        key={`bank ${option.transfer_method_id}`}
                        value={option.transfer_method_id}
                      >
                        {name}
                        {' '}
                        - Created on
                        {dayjs(time).format('MMM D,YYYY')}
                        {' '}
                        at
                        {dayjs(time).format('hh:mm A ')}
                        {' '}
                        {zone}
                      </MenuItem>
                    );
                  })
                ) : (
                  <MenuItem key={`bank ${0}`} value="" />
                )}
              </TextField>
              <FormErrors errorMsgs={errorMsgs} errorField="transfer_method_id" />
            </Grid>
            <Grid item md={6} xs={12}>
              <div className={`${classes.right} ${classes.submit}`}>
                <Button
                  color="primary"
                  variant="contained"
                  size="large"
                  disabled={isFieldsDisabled() || !value.transfer_method_id || loading}
                  onClick={submit}
                  disableElevation
                >
                  {t('fe_earnings_and_payouts_page:payout_withdraw_dialog.withdraw')}
                </Button>
              </div>
            </Grid>
          </Grid>
        </DialogActions>
      )}
    </Dialog>
  );
};

PayoutWithdrawDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default withTranslation(['fe_earnings_and_payouts_page'])(PayoutWithdrawDialog);
